/**
 * Shared renderer for vocabulary context snippets — the saved
 * `context_sentence` (or `context_hint`) that anchors a card to where it
 * came from. Splits on `\n+` paragraph boundaries and applies book
 * typography (first-line indent on every paragraph after the first,
 * inter-paragraph spacing). Optionally bolds occurrences of the lemma
 * and its inflections.
 *
 * Use this anywhere a card's plain-text context_sentence is rendered:
 * Stage 1 isolated-sentence fallback, Stage 2.5 MCQ context hint, etc.
 *
 * Intentionally does NOT cover:
 *  - Stage 1 chapter mode — uses ChapterContextReader for full chapter HTML.
 *  - Cloze (Stage 4 / review queue) — needs its own treatment because the
 *    sentence carries an inline blank (`___`) that a paragraph splitter
 *    would mangle. Cloze passages get equivalent typography via the
 *    `.vocab-cloze-sentence` CSS rules in application.css.
 *
 * Props:
 *   text           — raw context string (may contain \n / \n\n).
 *   highlight      — optional lemma; matched-prefix inflections render bold.
 *   quoted         — wrap the entire passage in curly double-quotes
 *                    (default true; passes false for already-quoted text).
 *   className      — wrapper styles (font, color, etc.) — caller-controlled.
 */
import { lemmaPrefix } from '../../../lib/lemmaPrefix';
import { cn } from '../../../lib/cn';

export default function ContextPassage({
  text,
  highlight,
  quoted = true,
  className = 'text-lg text-gray-500 dark:text-gray-400 font-serif italic leading-relaxed',
}) {
  if (!text) return null;

  const paragraphs = text
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (paragraphs.length === 0) return null;

  const renderInline = highlight ? makeHighlighter(highlight) : (s) => s;

  return (
    // max-w-[34rem] mx-auto keeps long passages narrower than the
    // SessionShell's max-w-2xl (42rem) progress bar above. Short snippets
    // don't reach the cap so they render at natural width.
    <div className={cn('max-w-[34rem] mx-auto', className)}>
      {paragraphs.map((para, i) => {
        const isFirst = i === 0;
        const isLast = i === paragraphs.length - 1;
        return (
          <p
            key={i}
            style={{
              textIndent: isFirst ? 0 : '2em',
              marginBottom: isLast ? 0 : '0.5em',
            }}
          >
            {quoted && isFirst && '“'}
            {renderInline(para)}
            {quoted && isLast && '”'}
          </p>
        );
      })}
    </div>
  );
}

function makeHighlighter(lemma) {
  const prefix = lemmaPrefix(lemma);
  if (!prefix) return (s) => s;
  const splitter = new RegExp(`(\\b${escapeRegex(prefix)}\\w*\\b)`, 'gi');
  const matcher = new RegExp(`^\\b${escapeRegex(prefix)}\\w*\\b$`, 'i');
  return (sentence) => {
    const parts = sentence.split(splitter);
    return parts.map((part, i) =>
      matcher.test(part) ? (
        <strong key={i} className="font-semibold text-gray-900 dark:text-white">
          {part}
        </strong>
      ) : (
        part
      )
    );
  };
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
